import { Badge } from "@/components/ui/badge";
import {
  ShieldAlert,
  Shield,
  ShieldCheck,
  Repeat,
  Network,
  RotateCcw,
  Target,
  CalendarClock,
  Server,
  KeySquare,
} from "lucide-react";

const CATEGORY_META = {
  retry_amplification: { label: "Retry amplification", icon: Repeat },
  cascading_failure: { label: "Cascading failure", icon: Network },
  missing_rollback: { label: "Missing rollback", icon: RotateCcw },
  high_blast_radius: { label: "High blast radius", icon: Target },
  change_window_conflict: { label: "Change-window conflict", icon: CalendarClock },
  unsafe_production_execution: { label: "Unsafe production execution", icon: Server },
  missing_approval_gate: { label: "Missing approval gate", icon: KeySquare },
};

const SEV_TONE = {
  block: "border-rose-500/50 text-rose-300 bg-rose-500/10",
  warn: "border-amber-500/50 text-amber-300 bg-amber-500/10",
  info: "border-sky-500/40 text-sky-300 bg-sky-500/5",
};

const RISK_TONE = {
  low: "text-emerald-300",
  medium: "text-amber-300",
  high: "text-rose-300",
};

export default function GuardrailPanel({ review }) {
  if (!review) return null;
  const RiskIcon =
    review.overall_risk === "high"
      ? ShieldAlert
      : review.overall_risk === "medium"
        ? Shield
        : ShieldCheck;

  return (
    <div className="cre-surface rounded-sm p-5 md:p-6" data-testid="guardrail-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div
            className={`h-10 w-10 rounded-sm border flex items-center justify-center ${
              review.overall_risk === "high"
                ? "border-rose-500/40 bg-rose-500/10"
                : review.overall_risk === "medium"
                  ? "border-amber-500/40 bg-amber-500/10"
                  : "border-emerald-500/40 bg-emerald-500/10"
            }`}
          >
            <RiskIcon
              className={`h-5 w-5 ${RISK_TONE[review.overall_risk]}`}
              strokeWidth={1.5}
            />
          </div>
          <div>
            <div className="cre-overline text-blue-300">
              {review.agent_name}
            </div>
            <h3 className="font-heading text-lg font-bold tracking-tight">
              Safety guardrail review
            </h3>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge
            variant="outline"
            className={`rounded-sm font-mono uppercase border ${
              review.overall_risk === "high"
                ? "border-rose-500/40 text-rose-300 bg-rose-500/5"
                : review.overall_risk === "medium"
                  ? "border-amber-500/40 text-amber-300 bg-amber-500/5"
                  : "border-emerald-500/40 text-emerald-300 bg-emerald-500/5"
            }`}
          >
            {review.overall_risk} risk
          </Badge>
          {review.blocking ? (
            <Badge
              variant="outline"
              className="rounded-sm font-mono uppercase border-rose-500/60 text-rose-200 bg-rose-500/15"
            >
              blocking
            </Badge>
          ) : null}
          <span
            className={`text-[10px] font-mono uppercase tracking-widest ${
              review.source === "llm" ? "text-emerald-400" : "text-amber-400"
            }`}
          >
            · {review.source}
          </span>
        </div>
      </div>

      <p className="mt-3 text-sm text-zinc-300 leading-relaxed">
        {review.summary}
      </p>

      {review.flags?.length ? (
        <div className="mt-4 space-y-2">
          {review.flags.map((f, i) => {
            const meta = CATEGORY_META[f.category] || {
              label: f.category,
              icon: ShieldAlert,
            };
            const Icon = meta.icon;
            return (
              <div
                key={i}
                data-testid={`guardrail-flag-${i}`}
                className={`border rounded-sm p-3 ${SEV_TONE[f.severity] || ""}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-start gap-2 min-w-0">
                    <Icon className="h-4 w-4 mt-0.5 shrink-0" strokeWidth={1.75} />
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-zinc-100">
                        {f.title}
                      </div>
                      <div className="text-[11px] font-mono text-zinc-400 mt-0.5">
                        {meta.label}
                      </div>
                    </div>
                  </div>
                  <Badge
                    variant="outline"
                    className="rounded-sm font-mono text-[10px] uppercase border-white/15"
                  >
                    {f.severity}
                  </Badge>
                </div>
                <p className="mt-2 text-xs text-zinc-300 leading-relaxed">
                  {f.detail}
                </p>
                {f.references?.length ? (
                  <div className="mt-1 text-[11px] font-mono text-zinc-500">
                    ref: {f.references.join(" · ")}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="mt-4 text-sm text-zinc-400">
          No safety flags raised — proposed remediation looks reversible and
          scoped.
        </div>
      )}
    </div>
  );
}
